import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-c40f3b74"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "confirmation-content"
}

import { numberToHex } from 'web3-utils'
import { useRoute, useRouter } from 'vue-router'
import {
  ref,
  onMounted,
  computed,
  reactive,
  toRaw,
  watch,
  nextTick,
  onBeforeMount,
} from 'vue'

import { InternalMethods } from '@/shared/apis/messages'
import Spinner from '@/popup/components/shared/Spinner.vue'
import ScrollToBottom from '@/popup/components/shared/Scroll/ScrollToBottom.vue'
import Button from '@/popup/components/shared/Button.vue'
import { useWeb3Eth } from '@/popup/compositions/useWeb3Eth'
import { useApprovalsStore } from '@/popup/store/ApprovalsStore'
import { useAppStore } from '@/popup/store/AppStore'
import { useGlobals } from '@/popup/compositions/useGlobals'
import TransactionContent from '@/popup/components/Transaction/TransactionContent.vue'
import { useFixedHeader } from '@/popup/compositions/useFixedHeader'
import IconButton from '@/popup/components/shared/IconButton.vue'
import MaxPriorityFeePerGasSlider from '@/popup/components/views/Confirmation/MaxPriorityFeePerGasSlider.vue'
import KeyUsage from '@/popup/components/views/Confirmation/KeyUsage.vue'
import QuotaUsage from '@/popup/components/views/Confirmation/QuotaUsage.vue'
import { redirectAfterApproval } from '@/popup/utils/redirectAfterApproval'
import { hasRelayerGasQuota } from '@/shared/relayer'
import { PopupDatabase, GlobalDatabase } from '@/popup/storage/Database'
import { requestModification } from '@/shared/apis/transactionChanges'
import {
  Transaction,
  TransactionChangeMessage,
  TransactionComponent,
  TransactionIntermediateData,
} from '@/shared/models/Transaction'
import { Approval, ApprovalApi, APPROVAL_ACTION } from '@/shared/apis/approvals'
import { GasProvider } from '@/shared/models/GasProvider'
import { RelayServiceDBSchema } from '@/shared/Database/UPSettings/models'
import { EXTENSION_ORIGIN } from '@/shared/config'
import { EoaAddress } from '@/shared/models/Profile'
import { QuotaDTO } from '@/business-logic/relay-service'
import { logError } from '@/shared/logger'
import { usePreferencesStore } from '@/popup/store/PreferencesStore'

const CONTENT_SCREEN_THRESHOLD = 420 // extension height - bottom buttons container height - top header height


export default /*#__PURE__*/_defineComponent({
  __name: 'Confirmation',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const id = route.params['id']
const approvalsStore = useApprovalsStore()
const appStore = useAppStore()
const preferences = usePreferencesStore()

const web3Eth = useWeb3Eth().getWeb3Eth()
const { getBalance } = web3Eth
const approval = approvalsStore.getApproval(id) as Approval<
  keyof ApprovalApi,
  Transaction
>
const transaction = reactive<Transaction>(approval.requestData)
const buttonsRef = ref<InstanceType<typeof HTMLElement>>()
const components = ref<TransactionComponent[]>(transaction.components ?? [])
const keyBalance = ref<number>()
const upExtensionControllerAddress = ref<EoaAddress | undefined>(
  appStore.upExtensionControllerAddress
)
const estimateGasLimit = ref<string | undefined>()
const onMountedRun = ref(false)
const showScrollToBottom = ref(false)
const isOverBalance = ref(true)
const scrollOffset = ref<number>(100)
const contentRef = ref<InstanceType<typeof HTMLElement>>()
const {
  onContentScroll,
  isFixedHeader,
  scrollPercentage,
  bottomThresholdOffset,
  isScrollable,
} = useFixedHeader(scrollOffset, contentRef)

const relayerQuota = ref<QuotaDTO>()
const allRelayers = ref<RelayServiceDBSchema[]>([])
const networkMaxPriorityFeePerGas = ref<string>()

const isControllerTransaction = computed(
  () => transaction.gasProvider?.type === 'controller'
)

const transactionFee = computed(() => {
  const { baseFee, maxFeePerGas, gasLimit: _gasLimit } = transaction

  const gasLimit = Number.parseInt(_gasLimit || '0')

  if (!baseFee || !gasLimit) {
    return 0
  }

  return Number.parseInt(maxFeePerGas || '0') * gasLimit
})

const scrollToBottom = () => {
  if (buttonsRef.value) {
    buttonsRef.value.scrollIntoView({ behavior: 'smooth' })
  }
}

/**
 * Convert raw transaction data to viewable transaction data.
 * Populates some of the reactive properties.
 *
 * @param transactionData raw transaction data from background
 */
const parseTransaction = (transactionData: TransactionIntermediateData) => {
  //fields set only once (components, initial gas price)
  if (components.value.length === 0) {
    components.value = transactionData.components || []
  }
  if (
    !networkMaxPriorityFeePerGas.value &&
    transactionData.networkMaxPriorityFeePerGas
  ) {
    networkMaxPriorityFeePerGas.value =
      transactionData.networkMaxPriorityFeePerGas
  }

  //dynamic(potentially) fields
  estimateGasLimit.value = transactionData.estimateGasLimit
  relayerQuota.value = transactionData.relayerQuota
  transaction.data = transactionData.data

  // Gas provider may change e.g. if relay service is not supported there is a fallback to controller address
  if (transactionData.gasProvider) {
    transaction.gasProvider = transactionData.gasProvider
  }

  transaction.maxPriorityFeePerGas =
    transactionData.maxPriorityFeePerGas ||
    transactionData.networkMaxPriorityFeePerGas

  if (transactionData.maxFeePerGas !== undefined) {
    transaction.maxFeePerGas = transactionData.maxFeePerGas
  }

  if (!transaction.gasLimit || transaction.gasLimit === '0') {
    transaction.gasLimit = transactionData.estimateGasLimit
  }

  if (transactionData.error) {
    transaction.error = transactionData.error
    transaction.status = 'FAILED'
  } else {
    transaction.error = undefined
    transaction.status = 'INITIALIZED'
  }
}

/**
 * Request a modification of the transaction data. In some cases we need to re-validate
 * the transaction data inside of the background. requestModification will return true in those
 * cases
 *
 * @param key - the property to modify
 * @param value - the value to set the property to
 */
const onModifyTransaction = async (
  message: TransactionChangeMessage | undefined,
  forceRecalculate = false
) => {
  if (
    (message && requestModification(transaction, message.key, message.value)) ||
    forceRecalculate
  ) {
    const transactionData = await useGlobals().sendMessage({
      method: InternalMethods.RECALCULATE_TRANSACTION_DATA,
      payload: {
        requestId: transaction.requestId,
        options: toRaw(transaction.options || []),
      },
    })
    parseTransaction(transactionData)
  }
}

const onGasProviderChange = async (provider: GasProvider) => {
  if (provider.type === 'controller') {
    fetchControllerBalance(provider.id)
  }

  await onModifyTransaction({
    key: 'gasProvider',
    value: provider,
  })
}

const setTransactionError = (error: Error) => {
  if (!transaction.error) {
    transaction.error = error
  }
}

const fetchControllerBalance = (controllerAddress?: string) => {
  const _controllerAddress =
    controllerAddress ?? upExtensionControllerAddress.value
  if (_controllerAddress) {
    getBalance(_controllerAddress)
      .then(balance => {
        nextTick(() => {
          keyBalance.value = Number.parseFloat(balance)
          isOverBalance.value = transactionFee.value > keyBalance.value
        })
      })
      .catch(error => {
        nextTick(() => {
          setTransactionError(error)
          keyBalance.value = 0
          isOverBalance.value = true
          logError(
            'Failed to fetch controller address balance.',
            _controllerAddress,
            error
          )
        })
      })
  }
}

watch(bottomThresholdOffset, value => {
  showScrollToBottom.value = value > 80 && isScrollable.value
})

onBeforeMount(() => {
  // prepopulates fields with data from transaction
  parseTransaction(transaction as TransactionIntermediateData)
})

onMounted(async () => {
  fetchControllerBalance()
  GlobalDatabase.getUserRelayers(numberToHex(appStore.network.chainId))
    .then(value => {
      allRelayers.value = value
    })
    .catch(() => {
      // This error should not be blocking. There's still a controller as a provider.
      logError('Failed to fetch relay services in during confirmation.')
    })

  // `.. === false` as it can be undefined and we are interested in `false` value only.
  // Kind of tightly coupled to `if` statement in `ethSendTransaction` that avoids recalculation if `isKeyringUnlocked` is
  // `false` for performance reasons making sure that recalculation runs only once either in `ethSendTransaction` or here.
  if (
    (transaction as TransactionIntermediateData)?.isKeyringUnlocked === false
  ) {
    await onModifyTransaction(undefined, true)
  }
  onMountedRun.value = true
})

const onSubmit = async (action: APPROVAL_ACTION) => {
  if (action === 'CONFIRMED') {
    await PopupDatabase.setDefaultGasProvider(
      appStore.currentProfile.address,
      transaction.gasProvider?.id
    )
  }

  const approvalDirective = await useGlobals().sendMessage({
    method: InternalMethods.HANDLE_APPROVAL,
    payload: {
      action,
      requestId: approval.id,
      data: {
        ...transaction,
        components: toRaw(components.value || []),
        options: toRaw(transaction.options || []),
      } as ApprovalApi['transaction']['input'],
    },
  })
  approvalsStore.processDirective(approvalDirective)

  if (
    components.value.some(component =>
      ['SetData', 'SetDataBatch'].includes(component.component)
    )
  ) {
    if (action === 'REJECTED') {
      appStore.removeTemporaryProfile(approval.requestData.from)
    } else {
      return redirectAfterApproval(
        router,
        approvalsStore.nonStashedApprovals.length,
        '/home'
      )
    }
  }

  redirectAfterApproval(
    router,
    approvalsStore.nonStashedApprovals.length,
    appStore.lastPopupState?.path
  )
}

const onLowBalanceCancelClick = async () => {
  const approvalDirective = await useGlobals().sendMessage({
    method: InternalMethods.HANDLE_APPROVAL,
    payload: {
      action: 'REJECTED',
      requestId: approval.id,
      data: {
        ...transaction,
        components: toRaw(components.value || []),
        options: toRaw(transaction.options || []),
      } as ApprovalApi['transaction']['input'],
    },
  })
  approvalsStore.processDirective(approvalDirective)
  if (
    components.value.some(component =>
      ['SetData', 'SetDataBatch'].includes(component.component)
    )
  ) {
    appStore.removeTemporaryProfile(approval.requestData.from)
  }
  router.push({
    path: `/controllerDetail/${transaction.gasProvider?.id}`,
    query: {
      reason: 'for-funding',
    },
  })
}

const onIframeLoaded = () => {
  if (buttonsRef.value) {
    buttonsRef.value.style.opacity = '1'
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (!onMountedRun.value)
      ? (_openBlock(), _createBlock(Spinner, {
          key: 0,
          class: "confirmation-spinner",
          "data-testid": "spinner"
        }))
      : _createCommentVNode("v-if", true),
    _createElementVNode("section", {
      ref_key: "contentRef",
      ref: contentRef,
      class: _normalizeClass(`confirmation-window ${onMountedRun.value ? 'loaded' : ''}`),
      onScrollPassive: _cache[8] || (_cache[8] = 
//@ts-ignore
(...args) => (_unref(onContentScroll) && _unref(onContentScroll)(...args)))
    }, [
      (components.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(TransactionContent, {
              transaction: transaction,
              components: components.value,
              preferences: _unref(preferences),
              "is-fixed-header": _unref(isFixedHeader),
              "scroll-percentage": _unref(scrollPercentage),
              "is-confirmation": true,
              class: "top-part",
              onOnIframeLoaded: onIframeLoaded,
              onOnTransactionChange: onModifyTransaction,
              onOnLowBalanceCancelClick: onLowBalanceCancelClick,
              onOnContainerHeightResize: _cache[4] || (_cache[4] = 
          height => {
            showScrollToBottom.value = height > CONTENT_SCREEN_THRESHOLD
          }
        ),
              onHeaderOffset: _cache[5] || (_cache[5] = 
          value => {
            scrollOffset.value = value
          }
        )
            }, {
              leftActions: _withCtx(() => [
                (transaction.origin !== _unref(EXTENSION_ORIGIN))
                  ? (_openBlock(), _createBlock(IconButton, {
                      key: 0,
                      class: "button-stash",
                      name: "arrow-left-lg",
                      tooltip: _ctx.$t('sign.minimalize'),
                      onOnClick: _cache[0] || (_cache[0] = ($event: any) => (onSubmit('STASHED')))
                    }, null, 8 /* PROPS */, ["tooltip"]))
                  : _createCommentVNode("v-if", true)
              ]),
              headerBottom: _withCtx(({ isFixed }) => [
                _createElementVNode("div", {
                  class: _normalizeClass(["header-bottom", { 'is-fixed': isFixed }])
                }, [
                  (isControllerTransaction.value && !transaction.error)
                    ? (_openBlock(), _createBlock(MaxPriorityFeePerGasSlider, {
                        key: 0,
                        class: _normalizeClass({ 'is-fixed': isFixed }),
                        "network-max-priority-fee-per-gas": networkMaxPriorityFeePerGas.value,
                        "max-priority-fee-per-gas": transaction.maxPriorityFeePerGas,
                        onChangeMaxPriorityFeePerGas: _cache[1] || (_cache[1] = 
                value =>
                  onModifyTransaction(
                    {
                      key: 'maxPriorityFeePerGas',
                      value: value.toString(),
                    },
                    true
                  )
              ),
                        onUpdateMaxPriorityFeePerGas: _cache[2] || (_cache[2] = 
                value =>
                  onModifyTransaction({
                    key: 'maxPriorityFeePerGas',
                    value: value.toString(),
                  })
              )
                      }, null, 8 /* PROPS */, ["class", "network-max-priority-fee-per-gas", "max-priority-fee-per-gas"]))
                    : _createCommentVNode("v-if", true),
                  (isControllerTransaction.value && keyBalance.value !== undefined)
                    ? (_openBlock(), _createBlock(KeyUsage, {
                        key: 1,
                        class: _normalizeClass({ 'is-fixed': isFixed }),
                        balance: keyBalance.value,
                        "transaction-fee": transactionFee.value,
                        "current-profile": _unref(appStore).currentProfile,
                        "all-relayers": allRelayers.value,
                        "selected-gas-provider": transaction.gasProvider!,
                        onOnGasProviderChange: onGasProviderChange,
                        onOverBalance: _cache[3] || (_cache[3] = value => (isOverBalance.value = value)),
                        onOnLowBalanceCancelClick: onLowBalanceCancelClick
                      }, null, 8 /* PROPS */, ["class", "balance", "transaction-fee", "current-profile", "all-relayers", "selected-gas-provider"]))
                    : (transaction.gasProvider && keyBalance.value !== undefined)
                      ? (_openBlock(), _createBlock(QuotaUsage, {
                          key: 2,
                          class: _normalizeClass({ 'is-fixed': isFixed }),
                          "estimate-gas-limit": estimateGasLimit.value,
                          "relayer-quota": relayerQuota.value,
                          balance: keyBalance.value,
                          "transaction-fee": transactionFee.value,
                          "current-profile": _unref(appStore).currentProfile,
                          "all-relayers": allRelayers.value,
                          "selected-gas-provider": transaction.gasProvider,
                          onOnGasProviderChange: onGasProviderChange
                        }, null, 8 /* PROPS */, ["class", "estimate-gas-limit", "relayer-quota", "balance", "transaction-fee", "current-profile", "all-relayers", "selected-gas-provider"]))
                      : _createCommentVNode("v-if", true)
                ], 2 /* CLASS */)
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["transaction", "components", "preferences", "is-fixed-header", "scroll-percentage"]),
            _createVNode(ScrollToBottom, {
              "show-scroll-button": showScrollToBottom.value,
              onScrollToBottom: scrollToBottom
            }, null, 8 /* PROPS */, ["show-scroll-button"]),
            _createElementVNode("div", {
              class: _normalizeClass(`bottom-part ${
          transaction.value && !transaction.data ? 'pt-0' : ''
        }`)
            }, [
              _createElementVNode("div", {
                ref_key: "buttonsRef",
                ref: buttonsRef,
                class: "buttons"
              }, [
                _createVNode(Button, {
                  "data-testid": "transaction-cancel-button",
                  variant: "text",
                  "is-full-width": true,
                  onClick: _cache[6] || (_cache[6] = ($event: any) => (onSubmit('REJECTED')))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('confirmation.cancel')), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }),
                _createVNode(Button, {
                  variant: "primary",
                  "is-full-width": true,
                  "is-long-press": true,
                  disabled: 
              !!transaction.error ||
              (isControllerTransaction.value && isOverBalance.value) ||
              (!isControllerTransaction.value &&
                !_unref(hasRelayerGasQuota)(relayerQuota.value, estimateGasLimit.value))
            ,
                  onOnLongPressComplete: _cache[7] || (_cache[7] = ($event: any) => (onSubmit('CONFIRMED')))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('confirmation.confirm')), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["disabled"])
              ], 512 /* NEED_PATCH */)
            ], 2 /* CLASS */)
          ]))
        : _createCommentVNode("v-if", true)
    ], 34 /* CLASS, NEED_HYDRATION */)
  ], 64 /* STABLE_FRAGMENT */))
}
}

})